<template>
    <Transition name="fade">
        <PermissionDialog :is-open="showMicPermissionAide" @cancel="handleMicPermissionDialogClick" @click="() => handleMicPermissionDialogClick({ useText: false })" />
    </Transition>
    <RecordingTestDialog :event-slug="coachingSessionUuid" @dismiss="handleTextboxShow" @mic-selected="handleMicSelection" />
    <WorkDocumentUploadDialog
        v-if="visualDocumentAnalysisEnabled"
        v-model:file="pendingWorkDocument"
        :open="isWorkDocumentDialogOpen"
        :capabilities-url="workDocumentCapabilitiesUrl"
        :can-add="!isFileUploadLimit"
        @added="handleWorkDocumentAdded"
        @read-failed="handleWorkDocumentReadFailed"
        @update:open="handleWorkDocumentDialogOpen"
        @selection-cleared="readerFallbackGeneration += 1"
        @upload-error="(message: string) => emit('upload-error', message)" />
    <ConfirmationDialog
        v-if="piiDetected && !piiBlocked"
        v-model:open="showPiiModal"
        :title="t('chat.controls.pii.warningTitle')"
        :text="piiMessage"
        :description="piiMessage"
        :confirm-label="t('chat.controls.pii.sendAnyway')"
        :cancel-label="t('chat.controls.pii.goBackAndEdit')"
        @confirm="handlePiiModalConfirm"
        @cancel="handlePiiEdited(PII_WARN)" />
    <BaseDialog v-if="piiBlocked" v-model:open="showPiiModal" :title="t('chat.controls.pii.blockedTitle')" :description="piiMessage" @close="handlePiiEdited(PII_BLOCK)">
        <div class="body text-ink-muted" data-testid="dialog-text">{{ piiMessage }}</div>
        <template #footer>
            <BaseButton
                theme="tertiary"
                @click="
                    showPiiModal = false;
                    handlePiiEdited(PII_BLOCK);
                "
                >{{ t("chat.controls.pii.goBackAndEdit") }}</BaseButton
            >
        </template>
    </BaseDialog>
    <div aria-live="polite">
        <StatusBadge :is-showing="shouldShowBadge" :text="statusBadgeText" :offset-y="8" />
    </div>
    <form
        v-bind="$attrs"
        ref="chatMessageControls"
        :aria-label="t('chat.controls.formLabel')"
        class="bg-surface-raised relative"
        :class="[
            { 'chat-message-controls--disabled': isDisabled },
            isNadiaChatRefreshEnabled
                ? 'nadia-chat-refresh-composer'
                : 'border border-control-border focus-within:border-control-border-focus focus-within:shadow-sm p-3 md:pt-6 rounded-lgr',
            { 'nadia-chat-refresh-composer-expanded': isNadiaChatRefreshEnabled && refreshComposerHasExpandedRadius },
        ]"
        :aria-disabled="isDisabled"
        data-testid="chat-input"
        @submit.prevent>
        <ComposerMentions
            v-model:text="textContent"
            v-model:mentions="composerMentions"
            :textarea="textarea"
            :enabled="composerMentionsAvailable"
            :disabled="isDisabled"
            @team-created="emit('team-created')" />
        <template v-if="isNadiaChatRefreshEnabled">
            <!-- eslint-disable-next-line vuejs-accessibility/form-control-has-label -->
            <output class="sr-only" aria-live="polite">{{ isRecording ? t("chat.composer.recordingAnnouncement") : "" }}</output>
            <template v-if="isRecording || isInitializing">
                <div class="flex items-center gap-2" aria-live="off">
                    <BaseButton
                        ref="refreshRecordingCancelButton"
                        theme="ghost"
                        size="sm"
                        icon-only
                        class="size-8 shrink-0 rounded-full p-0 text-xl/none"
                        :disabled="isDisabled"
                        type="button"
                        :title="t('chat.composer.cancelRecording')"
                        :aria-label="t('chat.composer.cancelRecording')"
                        @click="handleRecordingCancel">
                        <i class="bi bi-x-lg" aria-hidden="true" />
                    </BaseButton>
                    <div class="grow overflow-hidden">
                        <VoiceLevels :levels="isInitializing ? [] : recordingAudioLevelHistory" variant="dots" />
                    </div>
                    <BaseButton
                        theme="primary"
                        size="sm"
                        icon-only
                        class="composer-send ml-1 size-8 shrink-0 rounded-full p-0"
                        :disabled="isDisabled || isInitializing"
                        type="button"
                        :title="t('chat.composer.sendRecording')"
                        :aria-label="t('chat.composer.sendRecording')"
                        @click="recorder.stop()">
                        <i class="bi bi-arrow-up text-xl" aria-hidden="true" />
                    </BaseButton>
                </div>
            </template>
            <template v-else>
                <Transition name="fade">
                    <div v-if="isDropzoneVisible" class="dropzone flex items-center justify-center text-control-accent">
                        <p class="body-small-bold">{{ t("chat.composer.dropFiles") }}</p>
                    </div>
                </Transition>
                <!-- The region stays mounted so the first attachment is announced like the rest; empty:sr-only
                     keeps it out of the column's gap without leaving the accessibility tree. -->
                <div class="shrink-0 empty:sr-only" aria-live="polite">
                    <ul v-if="hasFiles" class="fs-mask flex justify-start gap-x-4 gap-y-2 flex-wrap p-1 max-h-96 overflow-y-auto pb-2">
                        <li v-for="file in draftFiles" :key="'id' in file ? file.id : 'draftId' in file ? file.draftId : file.name" class="min-w-0 max-w-full">
                            <FileAttachedTag :file="file" :is-removable="!isDisabled" data-testid="chat-control-file-attached-tag" @remove="removeAttachment(file)" />
                            <p v-if="'kind' in file && file.kind === 'visual_document'" class="body-small text-ink-muted mt-1 px-3" data-testid="document-read-status">
                                <i class="bi bi-check-circle mr-1" aria-hidden="true" />
                                {{ t("chat.composer.documentReadReady") }}
                            </p>
                        </li>
                    </ul>
                </div>
                <BaseNotification v-if="piiBlocked" theme="error" hide-dismiss>
                    <template #content>
                        <p class="body-small-semibold">{{ piiBannerMessage }}</p>
                    </template>
                </BaseNotification>
                <BaseNotification v-else-if="piiDetected && !piiBlocked" theme="warning" hide-dismiss>
                    <template #content>
                        <p class="body-small-semibold">{{ piiBannerMessage }}</p>
                    </template>
                </BaseNotification>
                <div class="nadia-refresh-composer-row" aria-live="polite" aria-relevant="text additions">
                    <div class="flex shrink-0 items-center gap-1">
                        <input
                            v-if="hasDocumentUpload"
                            :id="REFRESH_FILE_UPLOAD_INPUT_ID"
                            ref="fileInput"
                            data-testid="file-upload-input"
                            type="file"
                            aria-hidden
                            :disabled="isDisabled"
                            :accept="unifiedAcceptedFileTypes"
                            multiple
                            class="hidden"
                            @change="handleFileChange" />
                        <!-- eslint-disable-next-line vuejs-accessibility/label-has-for - label and file input share the same id constant -->
                        <label v-if="hasDocumentUpload" :for="REFRESH_FILE_UPLOAD_INPUT_ID" class="hidden">
                            {{ t("chat.composer.uploadFile") }}
                        </label>
                        <BaseTooltip
                            v-if="hasDocumentUpload"
                            v-model:open="isDocumentUploadTooltipShown"
                            :trigger-aria-label="t('chat.composer.uploadTooltipTrigger')"
                            :delay-duration="hasSeenDocumentUploadFeatureTooltip ? 0 : 10000">
                            <template #trigger>
                                <BaseButton
                                    v-if="!isUploading"
                                    theme="ghost"
                                    size="sm"
                                    icon-only
                                    class="size-8 shrink-0 rounded-full p-0 text-xl/none"
                                    :disabled="isDisabled || isTranscribing || isFileUploadLimit"
                                    type="button"
                                    :aria-label="uploadFileAriaLabel"
                                    @click="handleFileUploadClick">
                                    <i class="bi bi-plus-lg" aria-hidden="true" />
                                </BaseButton>
                                <LoadingSpinner v-else class="size-8 text-ink-muted" />
                            </template>
                            <template #content>{{ documentUploadTooltip }}</template>
                        </BaseTooltip>
                    </div>
                    <div class="nadia-refresh-composer-input fs-mask">
                        <!-- eslint-disable-next-line vuejs-accessibility/form-control-has-label - transcribing status is not a form field -->
                        <output v-if="isTranscribing" class="block min-h-8 px-1 py-1 leading-6 body-small text-control-placeholder">{{ t("chat.composer.transcribing") }}</output>
                        <template v-else>
                            <!-- eslint-disable-next-line vuejs-accessibility/label-has-for - label and textarea share the same id constant -->
                            <label :for="REFRESH_CHAT_INPUT_TEXTAREA_ID" class="sr-only">{{ t("chat.composer.messageInput") }}</label>
                            <div class="relative">
                                <PiiHighlightOverlay v-if="piiPatterns" :text="textContent" :warn-spans="warnSpans" :error-spans="errorSpans" compact />
                                <ComposerMentionHighlights
                                    v-if="paintInlineMentions"
                                    :text="textContent"
                                    :mentions="composerMentions"
                                    :textarea="textarea"
                                    :warn-spans="warnSpans"
                                    :error-spans="errorSpans" />
                                <BaseTextarea
                                    :id="REFRESH_CHAT_INPUT_TEXTAREA_ID"
                                    ref="composerTextarea"
                                    v-model:input="textContent"
                                    aria-live="off"
                                    autocomplete="off"
                                    :disabled="isDisabled"
                                    :placeholder="textPlaceholder"
                                    :maxlength="textareaMaxLength"
                                    :class="{ 'relative z-10': piiPatterns || hasInlineMentions, 'composer-textarea-mentioned': paintInlineMentions }"
                                    class="nadia-refresh-composer-textarea bg-transparent"
                                    @input="updateRefreshTextareaLines"
                                    @keydown="handleKeyDown"
                                    @keydown.enter="handleSendMessage"
                                    @paste="handlePaste" />
                            </div>
                            <!-- eslint-disable-next-line vuejs-accessibility/form-control-has-label -->
                            <output v-if="charCounterVisible" :class="charCounterColor" class="body-small text-right mt-1" :for="REFRESH_CHAT_INPUT_TEXTAREA_ID">
                                {{ `${textContent.length}/${textareaMaxLength}` }}
                            </output>
                        </template>
                    </div>
                    <BaseTooltip
                        v-if="voiceInputEnabled"
                        v-model:open="micTooltipOpen"
                        :trigger-aria-label="t('chat.composer.recordVoiceTooltipTrigger')"
                        :content-class="voiceNudgeVisible ? 'max-w-64 text-left' : undefined">
                        <template #trigger>
                            <BaseButton
                                ref="micButton"
                                theme="ghost"
                                size="sm"
                                icon-only
                                class="size-8 shrink-0 rounded-full p-0 text-xl/none"
                                :disabled="isMicDisabled"
                                type="button"
                                :aria-label="t('chat.composer.recordVoice')"
                                data-testid="record-voice"
                                :class="{ 'animate-mic-pulse': voiceNudgeVisible }"
                                @click="handleMicClick()">
                                <i class="bi bi-mic" aria-hidden="true" />
                            </BaseButton>
                        </template>
                        <template #content>{{ voiceNudgeVisible ? t("chat.composer.voiceNudgeTooltip") : t("chat.composer.recordVoice") }}</template>
                    </BaseTooltip>
                    <!-- The coach-mark tooltip is not announced on open, so this mirrors the old toast's
                         role="status". It stays mounted and empty: a region created with its text already
                         in it is not announced, so the copy must flip in while the region is present. -->
                    <!-- eslint-disable-next-line vuejs-accessibility/form-control-has-label - live status region, not a form field -->
                    <output class="sr-only" aria-live="polite" aria-atomic="true">{{ voiceNudgeVisible ? t("chat.composer.voiceNudgeTooltip") : "" }}</output>
                    <BaseButton
                        v-if="editAndStopGeneratingEnabled && waitingForAssistant"
                        theme="secondary"
                        size="sm"
                        icon-only
                        class="size-8 shrink-0 rounded-full p-0 text-xl/none"
                        type="button"
                        :aria-label="t('chat.composer.stopGenerating')"
                        data-testid="stop-generating"
                        @click="emit('stop-generating')">
                        <i class="bi bi-stop-fill" aria-hidden="true" />
                    </BaseButton>
                    <BaseTooltip v-else :trigger-aria-label="t('chat.composer.submitMessageTooltipTrigger')">
                        <template #trigger>
                            <BaseButton
                                theme="primary"
                                size="sm"
                                icon-only
                                class="composer-send ml-1 size-8 shrink-0 rounded-full p-0 text-xl/none"
                                :disabled="!canSubmit"
                                type="button"
                                :aria-label="t('chat.composer.sendMessage')"
                                data-testid="text-submit"
                                @click="handleSendMessage">
                                <ComposerSendGlyph class="rotate-180" />
                            </BaseButton>
                        </template>
                        <template #content>{{ t("chat.composer.sendMessage") }}</template>
                    </BaseTooltip>
                </div>
            </template>
        </template>

        <!-- Recording active (new voice composer only) -->
        <template v-else-if="betterVoiceComposerEnabled && (isRecording || isInitializing)">
            <!-- Waveform area — same slot as the textarea -->
            <div class="min-h-12 mb-2 px-3">
                <div class="flex items-center gap-3 py-3">
                    <span class="recording-dot shrink-0" aria-hidden="true" />
                    <!-- tabular-nums keeps digit widths fixed so the timer doesn't shift horizontally as 0:09 → 0:10 -->
                    <span class="body-semibold text-ink tabular-nums shrink-0" role="timer" aria-live="off">{{ recordingDisplayTime }}</span>
                    <div class="grow overflow-hidden">
                        <VoiceLevels
                            :levels="isInitializing ? [] : recordingAudioLevelHistory"
                            :bar-size="BAR_SIZE.SMALL"
                            :initial-display="VOICE_LEVEL_INITIAL_DISPLAY.OSCILLATING" />
                    </div>
                </div>
            </div>
            <!-- Bottom row — same slot as the button bar -->
            <div class="flex justify-end items-center gap-3 min-h-12">
                <div class="flex gap-3 items-center grow">
                    <BaseButton
                        theme="ghost"
                        icon-only
                        class="size-10 shrink-0 p-2.5 text-xl/none"
                        :disabled="isDisabled"
                        type="button"
                        :title="t('chat.composer.cancelRecording')"
                        :aria-label="t('chat.composer.cancelRecording')"
                        @click="handleRecordingCancel">
                        <i class="bi bi-x-lg" aria-hidden="true" />
                    </BaseButton>
                </div>
                <BaseButton
                    theme="primary"
                    icon-only
                    class="composer-send recording-send-pulse mr-2 size-10 shrink-0 rounded-full p-0"
                    :disabled="isDisabled"
                    type="button"
                    :title="t('chat.composer.sendRecording')"
                    :aria-label="t('chat.composer.sendRecording')"
                    @click="recorder.stop()">
                    <i class="bi bi-send-fill text-lg/none rotate-45 -translate-x-0.5" aria-hidden="true" />
                </BaseButton>
            </div>
        </template>

        <!-- Normal input -->
        <template v-else>
            <Transition name="fade">
                <div v-if="isDropzoneVisible" class="dropzone flex items-center justify-center text-control-accent">
                    <p class="body-small-bold">{{ t("chat.composer.dropFiles") }}</p>
                </div>
            </Transition>
            <!-- input: text area + transcribing state -->
            <div class="composer-input-area min-h-12 mb-2 px-3 fs-mask" aria-live="polite" aria-relevant="text additions">
                <ul v-if="hasFiles" class="shrink-0 flex justify-start gap-x-4 gap-y-2 flex-wrap p-1 max-h-96 overflow-y-auto pb-2">
                    <li v-for="file in draftFiles" :key="'id' in file ? file.id : 'draftId' in file ? file.draftId : file.name" class="min-w-0 max-w-full">
                        <FileAttachedTag :file="file" :is-removable="!isDisabled" data-testid="chat-control-file-attached-tag" @remove="removeAttachment(file)" />
                        <p v-if="'kind' in file && file.kind === 'visual_document'" class="body-small text-ink-muted mt-1 px-3" data-testid="document-read-status">
                            <i class="bi bi-check-circle mr-1" aria-hidden="true" />
                            {{ t("chat.composer.documentReadReady") }}
                        </p>
                    </li>
                </ul>
                <BaseNotification v-if="piiBlocked" theme="error" hide-dismiss class="mb-2">
                    <template #content>
                        <p class="body-small-semibold">{{ piiBannerMessage }}</p>
                    </template>
                </BaseNotification>
                <BaseNotification v-else-if="piiDetected && !piiBlocked" theme="warning" hide-dismiss class="mb-2">
                    <template #content>
                        <p class="body-small-semibold">{{ piiBannerMessage }}</p>
                    </template>
                </BaseNotification>
                <div v-if="isTranscribing" class="flex items-center gap-3 py-3">
                    <!-- eslint-disable-next-line vuejs-accessibility/form-control-has-label - transcribing status is not a form field -->
                    <output class="text-ink-muted body-small">{{ t("chat.composer.transcribing") }}</output>
                    <LoadingSpinner class="h-4 text-ink-muted" />
                </div>
                <template v-else-if="!betterVoiceComposerEnabled && !textboxVisible">
                    <div class="w-full">
                        <VoiceLevels
                            :class="{
                                'opacity-40 py-5': !isRecording && !isInitializing,
                                'opacity-70 py-5': isInitializing,
                                'py-4': isRecording,
                                'cursor-pointer': !isDisabled && !isRecording && !isInitializing,
                            }"
                            class="md:py-4"
                            :levels="isInitializing ? [] : recordingAudioLevelHistory"
                            :bar-size="isRecording ? BAR_SIZE.SMALL : BAR_SIZE.TINY"
                            :initial-display="VOICE_LEVEL_INITIAL_DISPLAY.OSCILLATING"
                            @click="!isDisabled && !isRecording && !isInitializing && (showTextbox = true)" />
                    </div>
                </template>
                <template v-else>
                    <!-- eslint-disable-next-line vuejs-accessibility/label-has-for - label is associated via for="chat-input-textarea" and textarea id -->
                    <label for="chat-input-textarea" class="sr-only">{{ t("chat.composer.messageInput") }}</label>
                    <div class="relative">
                        <PiiHighlightOverlay v-if="piiPatterns" :text="textContent" :warn-spans="warnSpans" :error-spans="errorSpans" />
                        <ComposerMentionHighlights
                            v-if="paintInlineMentions"
                            :text="textContent"
                            :mentions="composerMentions"
                            :textarea="textarea"
                            :warn-spans="warnSpans"
                            :error-spans="errorSpans" />
                        <BaseTextarea
                            id="chat-input-textarea"
                            ref="composerTextarea"
                            v-model:input="textContent"
                            aria-live="off"
                            autocomplete="off"
                            :disabled="isDisabled"
                            :placeholder="textPlaceholder"
                            :maxlength="textareaMaxLength"
                            :class="{ 'relative z-10': piiPatterns || hasInlineMentions, 'composer-textarea-mentioned': paintInlineMentions }"
                            class="max-h-[40vh] bg-transparent body mt-3"
                            @input="updateRefreshTextareaLines"
                            @keydown="handleKeyDown"
                            @keydown.enter="handleSendMessage"
                            @paste="handlePaste" />
                    </div>
                    <!-- eslint-disable-next-line vuejs-accessibility/form-control-has-label -->
                    <output v-if="charCounterVisible" :class="charCounterColor" class="text-right mt-1 body-small" for="chat-input-textarea">
                        <!-- eslint-disable-next-line @intlify/vue-i18n/no-raw-text -- punctuation separator -->
                        {{ textContent.length }}/{{ textareaMaxLength }}
                    </output>
                </template>
            </div>
            <div class="composer-bar flex justify-between items-center gap-3 min-h-12">
                <div class="flex min-w-0 flex-wrap gap-3 items-center grow">
                    <div class="flex items-center gap-3">
                        <!-- File Attachment -->
                        <input
                            v-if="hasDocumentUpload && !isUploading"
                            ref="fileInput"
                            data-testid="file-upload-input"
                            type="file"
                            aria-hidden
                            :disabled="isDisabled"
                            :accept="unifiedAcceptedFileTypes"
                            multiple
                            class="hidden"
                            @change="handleFileChange" />
                        <BaseTooltip
                            v-if="hasDocumentUpload"
                            v-model:open="isDocumentUploadTooltipShown"
                            :trigger-aria-label="t('chat.composer.uploadTooltipTrigger')"
                            :delay-duration="hasSeenDocumentUploadFeatureTooltip ? 0 : 10000">
                            <template #trigger>
                                <BaseButton
                                    v-if="!isUploading"
                                    theme="ghost"
                                    icon-only
                                    class="size-10 shrink-0 p-2.5 text-xl/none"
                                    :disabled="isDisabled || isTranscribing || isFileUploadLimit"
                                    type="button"
                                    :aria-label="uploadFileAriaLabel"
                                    @click="handleFileUploadClick">
                                    <i class="bi bi-paperclip" aria-hidden="true" />
                                </BaseButton>
                                <LoadingSpinner v-else class="min-w-10 h-6 text-ink-muted" />
                            </template>
                            <template #content>{{ documentUploadTooltip }}</template>
                        </BaseTooltip>

                        <!-- Dictation toggle -->
                        <BaseTooltip :trigger-aria-label="t('chat.controls.dictationToggleTooltipTrigger')">
                            <template #trigger>
                                <BaseButton
                                    :theme="useChatStore.dictationOn ? 'secondary' : 'ghost'"
                                    icon-only
                                    class="size-10 shrink-0 p-2.5 text-xl/none"
                                    type="button"
                                    :disabled="isDisabled"
                                    :aria-pressed="useChatStore.dictationOn"
                                    :aria-label="useChatStore.dictationOn ? t('chat.controls.turnOffSound') : t('chat.controls.turnOnSound')"
                                    @click="handleDictationToggle">
                                    <i class="bi bi-volume-up" aria-hidden="true" />
                                </BaseButton>
                            </template>
                            <template #content>
                                {{ useChatStore.dictationOn ? t("chat.controls.turnOffSound") : t("chat.controls.turnOnSound") }}
                            </template>
                        </BaseTooltip>
                    </div>

                    <!-- Dictation speed (visible whenever dictation is on) -->
                    <BaseTooltip v-if="useChatStore.dictationOn" :trigger-aria-label="t('chat.controls.dictationSpeedTooltipTrigger')">
                        <template #trigger>
                            <BaseButton
                                theme="ghost"
                                size="sm"
                                type="button"
                                :disabled="isDisabled"
                                :aria-label="t('chat.messageActions.playbackSpeedLabel', { speed: useChatStore.dictationSpeed })"
                                @click="cycleDictationSpeed">
                                <span aria-hidden="true">{{ t("chat.controls.speedValue", { speed: useChatStore.dictationSpeed }) }}</span>
                            </BaseButton>
                        </template>
                        <template #content>{{ t("chat.messageActions.playbackSpeed") }}</template>
                    </BaseTooltip>

                    <!-- Skip dictation (visible while dictation is on; disabled when nothing is playing) -->
                    <BaseTooltip v-if="useChatStore.dictationOn" :trigger-aria-label="t('chat.controls.skipDictationTooltipTrigger')">
                        <template #trigger>
                            <BaseButton
                                theme="ghost"
                                icon-only
                                class="size-10 shrink-0 p-2.5 text-xl/none"
                                type="button"
                                :disabled="isDisabled || !isDictationPlaying"
                                :aria-label="t('chat.controls.skipCurrentAudio')"
                                @click="skipDictation">
                                <i class="bi bi-skip-forward" aria-hidden="true"></i>
                            </BaseButton>
                        </template>
                        <template #content>{{ t("chat.controls.skip") }}</template>
                    </BaseTooltip>
                </div>

                <!-- New mode: mic shrinks when text is present; send grows from 0 -->
                <div v-if="betterVoiceComposerEnabled" class="flex items-center gap-2 shrink-0">
                    <template v-if="editAndStopGeneratingEnabled && waitingForAssistant">
                        <BaseButton
                            theme="secondary"
                            icon-only
                            class="size-10 shrink-0 p-2.5 text-xl/none"
                            type="button"
                            :aria-label="t('chat.composer.stopGenerating')"
                            data-testid="stop-generating"
                            @click="emit('stop-generating')">
                            <i class="bi bi-stop-fill" aria-hidden="true" />
                        </BaseButton>
                    </template>
                    <template v-else>
                        <BaseTooltip v-if="voiceInputEnabled" :trigger-aria-label="t('chat.composer.recordVoiceTooltipTrigger')">
                            <template #trigger>
                                <BaseButton
                                    theme="primary"
                                    icon-only
                                    :disabled="isDisabled || isTranscribing"
                                    type="button"
                                    :aria-label="t('chat.composer.recordVoice')"
                                    data-testid="record-voice"
                                    class="composer-send shrink-0 rounded-full p-0 transition-[width,height,background-color,opacity] duration-300 ease-out"
                                    :class="canSubmit ? 'h-10 w-10' : 'h-12 w-12'"
                                    @click="handleRecordingStart()">
                                    <i
                                        class="legacy-glyph bi bi-mic-fill transition-[font-size] duration-300"
                                        :class="canSubmit ? 'text-lg/none' : 'text-2xl/none'"
                                        aria-hidden="true" />
                                    <ComposerMicGlyph class="tonal-glyph" />
                                </BaseButton>
                            </template>
                            <template #content>{{ t("chat.composer.recordVoice") }}</template>
                        </BaseTooltip>
                        <BaseTooltip :trigger-aria-label="t('chat.composer.submitMessageTooltipTrigger')">
                            <template #trigger>
                                <BaseButton
                                    theme="primary"
                                    icon-only
                                    :disabled="!canSubmit"
                                    type="button"
                                    :aria-label="t('chat.composer.sendMessage')"
                                    data-testid="text-submit"
                                    class="composer-send shrink-0 rounded-full p-0 transition-[width,height,opacity,transform,background-color] duration-300 ease-out overflow-hidden"
                                    :class="composerSubmitSizeClass"
                                    @click="handleSendMessage">
                                    <i class="legacy-glyph bi bi-send-fill text-base/none rotate-45 -translate-x-0.5" aria-hidden="true" />
                                    <i class="tonal-glyph bi bi-arrow-up text-xl/none" aria-hidden="true" />
                                </BaseButton>
                            </template>
                            <template #content>{{ t("chat.composer.sendMessage") }}</template>
                        </BaseTooltip>
                    </template>
                </div>

                <!-- Old mode: submit when in text mode -->

                <!-- Old mode: input toggle -->
                <BaseTooltip v-if="!betterVoiceComposerEnabled && voiceInputEnabled" :trigger-aria-label="t('chat.controls.inputToggleTooltipTrigger')">
                    <template #trigger>
                        <BaseButton
                            ref="inputSourceToggle"
                            theme="ghost"
                            icon-only
                            class="size-10 shrink-0 p-2.5 text-xl/none"
                            :disabled="isDisabled || isRecording || isTranscribing"
                            type="button"
                            :aria-label="inputToggleTipText"
                            :aria-pressed="showTextbox"
                            data-testid="input-source"
                            @click="showTextbox = !showTextbox">
                            <i :class="showTextbox ? 'bi bi-mic' : 'bi bi-keyboard'" aria-hidden="true" />
                        </BaseButton>
                    </template>
                    <template #content>
                        {{ inputToggleTipText }}
                    </template>
                </BaseTooltip>

                <BaseButton
                    v-if="!betterVoiceComposerEnabled && textboxVisible && editAndStopGeneratingEnabled && waitingForAssistant"
                    theme="secondary"
                    icon-only
                    class="size-10 shrink-0 p-2.5 text-xl/none"
                    type="button"
                    :aria-label="t('chat.composer.stopGenerating')"
                    data-testid="stop-generating-legacy"
                    @click="emit('stop-generating')">
                    <i class="bi bi-stop-fill" aria-hidden="true" />
                </BaseButton>
                <BaseButton
                    v-else-if="!betterVoiceComposerEnabled && textboxVisible"
                    theme="primary"
                    :title="t('chat.controls.submit')"
                    :aria-label="t('chat.composer.sendMessage')"
                    :disabled="!canSubmit"
                    type="button"
                    data-testid="text-submit"
                    class="composer-send size-10 shrink-0 rounded-full p-0"
                    @click="handleSendMessage">
                    <ArrowCircle direction="up" background-class="fill-transparent" foreground-class="fill-current stroke-current" />
                </BaseButton>

                <div v-if="!betterVoiceComposerEnabled && !textboxVisible" class="shrink-0 flex items-center">
                    <BaseButton
                        v-if="isRecording"
                        theme="ghost"
                        icon-only
                        class="size-10 shrink-0 p-2.5 text-xl/none"
                        type="button"
                        :title="t('chat.composer.cancelRecording')"
                        :aria-label="t('chat.composer.cancelRecording')"
                        @click="handleRecordingCancel">
                        <i class="bi bi-x-lg" aria-hidden="true" />
                    </BaseButton>
                    <BaseButton
                        theme="primary"
                        type="button"
                        :disabled="isDisabled || isInitializing"
                        :class="isRecording ? 'recorder-button--recording' : 'recorder-button--idle'"
                        :aria-pressed="isRecording"
                        class="recorder-button body-bold"
                        @click="isRecording ? recorder.stop() : handleRecordingStart()">
                        <div class="h-10 md:h-6 w-10 md:w-6">
                            <StopCircle v-if="isRecording" background-class="md:fill-transparent fill-recording-indicator" foreground-class="fill-ink-inverse" />
                            <MicrophoneCircle v-else background-class="fill-accent md:fill-transparent" foreground-class="stroke-ink-inverse" />
                        </div>
                        <span class="md:inline hidden">
                            <template v-if="isInitializing">{{ t("chat.controls.initializing") }}</template>
                            <template v-else>{{ recordingButtonText }}</template>
                        </span>
                    </BaseButton>
                </div>
            </div>
        </template>
    </form>

    <!-- eslint-disable-next-line vuejs-accessibility/media-has-caption - the dictated text is displayed on screen -->
    <audio id="audioPlayer" ref="audioPlayer" class="hidden"></audio>
</template>

<script setup lang="ts">
import { AudioRecorder, msToDisplayTime } from "/js/AudioRecorder.js";
import { getCookie } from "/js/utils";
import { Capacitor } from "@capacitor/core";
import { usePage } from "@inertiajs/vue3";
import { unrefElement, useDropZone, useEventListener, useIntervalFn, useMediaQuery, useResizeObserver, useStorage } from "@vueuse/core";
import { fetchApi, useFetch } from "~vue/api";
import { isAccessBlocked, reloadWhenAccessBlocked } from "~vue/api/accessBlocked";
import type { ComposerMention, DictationRequest } from "~vue/api/generated/chat";
import type { ChatInputFile, GlobalProperties, OrganizationDetails } from "~vue/appTypes";
import BaseButton from "~vue/components/BaseButton.vue";
import BaseDialog from "~vue/components/BaseDialog.vue";
import BaseNotification from "~vue/components/BaseNotification.vue";
import BaseTooltip from "~vue/components/BaseTooltip.vue";
import ConfirmationDialog from "~vue/components/ConfirmationDialog.vue";
import FileAttachedTag from "~vue/components/FileAttachedTag.vue";
import BaseTextarea from "~vue/components/form/BaseTextarea.vue";
import StatusBadge from "~vue/components/StatusBadge.vue";
import VoiceLevels, { BAR_SIZE, INITIAL_DISPLAY as VOICE_LEVEL_INITIAL_DISPLAY } from "~vue/components/VoiceLevels.vue";
import { useChatInputPreference } from "~vue/composables/useChatInputPreference.js";
import { useDocumentIntake } from "~vue/composables/useDocumentIntake";
import { useMicConsent } from "~vue/composables/useMicConsent";
import { useNadiaChatRefreshEnabled } from "~vue/composables/useNadiaChatRefresh";
import { usePiiDetection } from "~vue/composables/usePiiDetection";
import { useTimedShow } from "~vue/composables/useTimedShow";
import type { AudioStats } from "~vue/composables/useTranscription";
import { CHAT_EVENT } from "~vue/events";
import ArrowCircle from "~vue/icons/ArrowCircle.vue";
import ComposerMicGlyph from "~vue/icons/composer/ComposerMicGlyph.vue";
import ComposerSendGlyph from "~vue/icons/composer/ComposerSendGlyph.vue";
import LoadingSpinner from "~vue/icons/LoadingSpinner.vue";
import MicrophoneCircle from "~vue/icons/MicrophoneCircle.vue";
import StopCircle from "~vue/icons/StopCircle.vue";
import PermissionDialog from "~vue/Onboarding/PermissionDialog.vue";
import RecordingTestDialog from "~vue/RecordingTestDialog.vue";
import { useChatStore } from "~vue/stores/chatStore";
import AmplitudeEventNames from "~vue/utils/amplitudeEventNames";
import { CHAT_MESSAGE_CONTROLS_LOCATION, FILE_UPLOAD_LIMIT } from "~vue/utils/constants";
import { refreshCsrfToken } from "~vue/utils/csrf";
import { formatAcceptedFileTypes, getAcceptedFileTypes, IMAGE_FILE_EXTENSIONS } from "~vue/utils/fileUpload";
import { isImeComposing } from "~vue/utils/keyboard";
import { logError, logUserInteraction } from "~vue/utils/logUtils";
import { MARKDOWN_CONTENT_TYPE, normalizeMarkdownFiles } from "~vue/utils/markdownFileUpload";
import { type PatternEntry, PII_BLOCK, PII_WARN } from "~vue/utils/pii/keywordClassifier";
import { withRequestId } from "~vue/utils/requestId";
import { isSessionEndedError, reportSessionEnded, SESSION_ENDED_ERROR_CAUSE, sessionEndedCodeFrom, wasGloballyHandled403 } from "~vue/utils/sessionEnded";
import { WAF_ERROR_CAUSE } from "~vue/utils/wafDetection";
import DOMPurify from "dompurify";
import { computed, inject, nextTick, onMounted, onUnmounted, type Ref, ref, useTemplateRef, watch } from "vue";
import { useI18n } from "vue-i18n";

import ComposerMentionHighlights from "./ComposerMentionHighlights.vue";
import ComposerMentions from "./ComposerMentions.vue";
import { reconcileMentions } from "./mentionOccurrences";
import PiiHighlightOverlay from "./PiiHighlightOverlay.vue";
import type { DictationChunk, RecordingChunk } from "./types";
import WorkDocumentUploadDialog from "./WorkDocumentUploadDialog.vue";

const { t } = useI18n();

const DOCUMENT_LENGTH_THRESHOLD = 1000;
const DOCUMENT_TYPES = [
    "text/plain",
    "text/csv",
    MARKDOWN_CONTENT_TYPE,
    "application/pdf",
    "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
    "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    "application/vnd.openxmlformats-officedocument.presentationml.presentation",
];
const IMAGE_TYPES = ["image/jpeg", "image/png", "image/webp", "image/gif"];
const ALL_SUPPORTED_FILE_TYPES = [...DOCUMENT_TYPES, ...IMAGE_TYPES];
const READER_FILE_EXTENSIONS = [".pdf", ...IMAGE_FILE_EXTENSIONS];
const REFRESH_CHAT_INPUT_TEXTAREA_ID = "chat-input-textarea";
const REFRESH_FILE_UPLOAD_INPUT_ID = "nadia-refresh-file-upload-input";

type ChatMessageControlProps = {
    uploadContextKey?: string;
    compact?: boolean;
    autoFocusInput?: boolean;
    isTranscribing?: boolean;
    isSending?: boolean;
    showSlowTranscription?: boolean;
    defaultTextInput?: boolean;
    isDisabled?: boolean;
    isPasteDisabled?: boolean;
    userMessageCount?: number;
    documentUploadEnabled?: boolean;
    visualDocumentAnalysisEnabled?: boolean;
    workDocumentCapabilitiesUrl?: string;
    betterVoiceComposerEnabled?: boolean;
    hideChatVoiceInputEnabled?: boolean;
    placeholder?: string;
    initialValue?: string;
    composerMentionsEnabled?: boolean;
    isFileUploadLimit?: boolean;
    userCoachExchangeId?: string | null;
    textareaMaxLength?: string;
    location?: string;
    preserveDraftOnSend?: boolean;
    piiPatterns?: PatternEntry[] | null;
    validatePiiOnSend?: boolean;
    waitingForAssistant?: boolean;
    editAndStopGeneratingEnabled?: boolean;
    showVoiceNudge?: boolean;
};

let uploadGeneration = 0;
let uploadOwnerActive = true;

const props = withDefaults(defineProps<ChatMessageControlProps>(), {
    compact: false,
    autoFocusInput: true,
    isTranscribing: false,
    isSending: false,
    showSlowTranscription: false,
    defaultTextInput: false,
    isDisabled: false,
    isPasteDisabled: false,
    userMessageCount: 0,
    documentUploadEnabled: false,
    visualDocumentAnalysisEnabled: false,
    workDocumentCapabilitiesUrl: "",
    betterVoiceComposerEnabled: false,
    hideChatVoiceInputEnabled: false,
    initialValue: "",
    composerMentionsEnabled: true,
    isFileUploadLimit: false,
    userCoachExchangeId: null,
    piiPatterns: null,
    validatePiiOnSend: false,
    preserveDraftOnSend: false,
    waitingForAssistant: false,
    editAndStopGeneratingEnabled: false,
    showVoiceNudge: false,
});

const chatMessageControls = useTemplateRef("chatMessageControls");
// this is the actual drop area where files are processed on drop
useDropZone(chatMessageControls, {
    onDrop: (files) => {
        void routeFiles(files, "drag_and_drop");
    },
    preventDefaultForUnhandled: true,
});

// this second dropzone triggers on the body element so that the drag an drop area on chat message control can be seen
const { isOverDropZone: isOverBody } = useDropZone(document.body, {
    preventDefaultForUnhandled: true,
});

const hasSeenDocumentUploadFeatureTooltip = useStorage("hasSeenDocumentUploadFeatureTooltip", false);
const userChatInputPreference = useChatInputPreference();
const chatRefreshEnabled = useNadiaChatRefreshEnabled();
const isNadiaChatRefreshEnabled = computed(() => props.compact || chatRefreshEnabled.value);
const hasDocumentUpload = computed(() => !!props.documentUploadEnabled && !!organizationDetails?.value?.is_document_upload_enabled);
const {
    routeFiles,
    reset: resetDocumentIntake,
    isWorkDocumentDialogOpen,
    pendingWorkDocument,
    readerFallbackGeneration,
    handleWorkDocumentAdded,
    handleWorkDocumentDialogOpen,
    handleWorkDocumentReadFailed,
} = useDocumentIntake({
    visualEnabled: () => props.visualDocumentAnalysisEnabled,
    uploadEnabled: () => hasDocumentUpload.value,
    disabled: () => props.isDisabled || isFileUploadLimit.value,
    remaining: () => FILE_UPLOAD_LIMIT - draftFiles.value.length,
    legacyLimit: (file) => fileSizeLimits.value[file.type] ?? defaultFileSizeLimit.value,
    upload: (files, source, fields, isStale) => handleUpload(files, source, fields, isStale),
    addVisual: (file) => {
        useChatStore.addFile(file);
        showTextbox.value = true;
    },
    error: (message) => emit("upload-error", message),
    notice: (message) => emit("upload-notice", message),
});

const emit = defineEmits<{
    "team-created": [];
    "recording-start": [];
    "recording-complete": [RecordingChunk];
    "recording-error": [];
    "textbox-show": [];
    "text-keydown": [];
    "text-send": [
        {
            content: string;
            files?: ChatInputFile[];
            mentions?: ComposerMention[];
        },
    ];
    "text-change": [content: string];
    "text-paste": [{ name: string; content: string; description: string }];
    "upload-error": [string | null];
    "upload-notice": [string];
    "stop-generating": [];
    "voice-nudge-activate": [];
    "voice-nudge-dismiss": [];
}>();

const { emitter, $feedbackMailto } = inject<GlobalProperties>("globalProperties", {} as GlobalProperties);
const coachingSessionId = inject("coachingSessionId", undefined);
const coachingSessionUuid = inject<string | undefined>("coachingSessionUuid", undefined);
const organizationDetails = inject<Ref<OrganizationDetails>>("organization_details");

const isLgBreakpoint = useMediaQuery("(min-width: 1024px)");

const recorder = ref(new AudioRecorder());
const isInitializing = ref(false);

// template refs
const audioPlayer = useTemplateRef("audioPlayer");
const refreshRecordingCancelButton = useTemplateRef<InstanceType<typeof BaseButton>>("refreshRecordingCancelButton");

// Dictation
const isDictationPlaying = ref(false);
const dictationChunks = ref<Record<string, Array<DictationChunk>>>({});
const nextDictationChunkId = ref<Partial<DictationChunk>>({});
const skippedMessageIds = new Set<string>();
const skippedLegacyDictationMessageIds = new Set<string>();
const pendingDictationTextRequests: Array<{
    data: {
        message_id: string;
        dictation_count: number;
        coaching_session_uuid: string;
        content: string;
        signature: string;
    };
    controller: AbortController;
    retries: number;
}> = [];
const dictationTextControllers = new Map<string, Map<AbortController, number>>();
const failedDictationSequences = new Map<string, Set<number>>();
// Mirrors MAX_IN_FLIGHT_SYNTHESIS in coach_api/coach_api/routers/chat/dictation.py.
const MAX_ACTIVE_DICTATION_TEXT_REQUESTS = 3;
let activeDictationTextRequests = 0;

const clearFailedDictationSequencesForOtherMessages = (messageId: string) => {
    for (const failedMessageId of failedDictationSequences.keys()) {
        if (failedMessageId !== messageId) {
            failedDictationSequences.delete(failedMessageId);
        }
    }
};

const removeDictationTextController = (messageId: string, controller: AbortController) => {
    const controllers = dictationTextControllers.get(messageId);
    controllers?.delete(controller);
    if (controllers?.size === 0) {
        dictationTextControllers.delete(messageId);
    }
};

const abortDictationTextRequests = (messageId: string) => {
    for (let index = pendingDictationTextRequests.length - 1; index >= 0; index -= 1) {
        const request = pendingDictationTextRequests[index];
        if (request.data.message_id === messageId) {
            request.controller.abort();
            pendingDictationTextRequests.splice(index, 1);
            removeDictationTextController(messageId, request.controller);
        }
    }
    for (const controller of dictationTextControllers.get(messageId)?.keys() ?? []) {
        controller.abort();
    }
    dictationTextControllers.delete(messageId);
};

const abortAllDictationTextRequests = (exceptMessageId?: string) => {
    const messageIds = new Set([...dictationTextControllers.keys(), ...pendingDictationTextRequests.map(({ data }) => data.message_id)]);
    for (const messageId of messageIds) {
        if (messageId !== exceptMessageId) {
            abortDictationTextRequests(messageId);
        }
    }
};

const markDictationTextRequestsAsFailed = () => {
    for (const [messageId, controllers] of dictationTextControllers) {
        const failedSequences = failedDictationSequences.get(messageId) ?? new Set<number>();
        for (const sequenceNumber of controllers.values()) {
            failedSequences.add(sequenceNumber);
        }
        if (failedSequences.size > 0) {
            failedDictationSequences.set(messageId, failedSequences);
        }
    }
};

const cancelOutstandingDictationText = () => {
    markDictationTextRequestsAsFailed();
    const messageIds = new Set([...pendingDictationTextRequests.map(({ data }) => data.message_id), ...dictationTextControllers.keys(), ...failedDictationSequences.keys()]);
    if (nextDictationChunkId.value.messageId) {
        messageIds.add(nextDictationChunkId.value.messageId);
    }
    if (useChatStore.dictationPlayingMessageId) {
        messageIds.add(useChatStore.dictationPlayingMessageId);
    }
    for (const messageId of messageIds) {
        skippedMessageIds.add(messageId);
    }
    abortAllDictationTextRequests();
};

const applyDictationSpeed = () => {
    if (audioPlayer.value) {
        audioPlayer.value.playbackRate = useChatStore.dictationSpeed;
    }
};

watch(() => useChatStore.dictationSpeed, applyDictationSpeed);

// Recording
const isRecording = ref(false);
const recordingAudioLevelHistory = ref<number[]>([]);
const showMicPermissionAide = ref(false);
const { micConsentBlocks } = useMicConsent();
const IDLE_MIC_RELEASE_MS = 30000;
let idleMicReleaseTimeout: ReturnType<typeof setTimeout> | undefined;
let componentActive = true;
let recordClickTime: number | null = null;
let initToStartMs: number | null = null;

// Recording timer
const recordingElapsedMs = ref(0);
let recordingStartTimestamp: number | null = null;

const recordingDisplayTime = computed(() => {
    const totalSeconds = Math.floor(recordingElapsedMs.value / 1000);
    const minutes = Math.floor(totalSeconds / 60);
    const seconds = totalSeconds % 60;
    return `${minutes}:${String(seconds).padStart(2, "0")}`;
});

const { pause: pauseRecordingTimer, resume: resumeRecordingTimer } = useIntervalFn(
    () => {
        if (recordingStartTimestamp != null) {
            recordingElapsedMs.value = Date.now() - recordingStartTimestamp;
        }
    },
    200,
    { immediate: false },
);

function startRecordingTimer() {
    recordingStartTimestamp = Date.now();
    recordingElapsedMs.value = 0;
    resumeRecordingTimer();
}

function stopRecordingTimer() {
    pauseRecordingTimer();
    recordingStartTimestamp = null;
    recordingElapsedMs.value = 0;
}

// Normally arrives via shared Inertia props, so a new page mounting this composer is gated by
// default. The prop is the override for anonymous feedback chat, where there is no org context.
const voiceInputEnabled = computed(() => !props.hideChatVoiceInputEnabled && !organizationDetails?.value?.is_chat_voice_input_hidden);

const micButton = useTemplateRef<InstanceType<typeof BaseButton>>("micButton");
const isMicDisabled = computed(() => props.isDisabled || props.isTranscribing || props.waitingForAssistant);
// Hold the coach-mark back while the mic can't be tapped (e.g. Nadia is still replying), so a
// keystroke can't retire a nudge the user had no way to act on.
const voiceNudgeVisible = computed(() => props.showVoiceNudge && voiceInputEnabled.value && !isMicDisabled.value);

// While the voice nudge is eligible the mic tooltip is pinned open as a coach-mark, so hovering off
// the mic can't close it early; otherwise it follows reka's hover/focus state. The model must be a
// boolean from mount: reka treats an initially undefined `open` as uncontrolled and would then
// ignore the pinned value once the pointer leaves.
const micTooltipHoverOpen = ref(false);
const micTooltipOpen = computed<boolean>({
    get: () => voiceNudgeVisible.value || micTooltipHoverOpen.value,
    set: (open) => {
        micTooltipHoverOpen.value = open;
    },
});

function handleMicClick() {
    if (voiceNudgeVisible.value) {
        emit("voice-nudge-activate");
    }
    handleRecordingStart();
}

// The coach-mark dismisses on the user's first interaction elsewhere; the mic's own click emits
// activate instead, so interactions originating on the mic are ignored here.
function dismissVoiceNudgeOnInteraction(event: Event) {
    if (!voiceNudgeVisible.value) {
        return;
    }
    // Escape is the keyboard way to close the pinned tooltip, so it dismisses even from the mic.
    const isEscape = event instanceof KeyboardEvent && event.key === "Escape";
    // Tab only moves focus; retiring the nudge on it would stop keyboard users reaching the mic to activate it.
    if (event instanceof KeyboardEvent && event.key === "Tab") {
        return;
    }
    if (!isEscape && event.target instanceof Node && unrefElement(micButton)?.contains(event.target)) {
        return;
    }
    emit("voice-nudge-dismiss");
}
useEventListener(globalThis.document, "pointerdown", dismissVoiceNudgeOnInteraction, { capture: true });
useEventListener(globalThis.document, "keydown", dismissVoiceNudgeOnInteraction, { capture: true });

// Initialize with stored preference, fallback to defaultTextInput prop
const showTextbox = ref(userChatInputPreference.value !== null ? userChatInputPreference.value : props.defaultTextInput);

// Without voice input the legacy composer has no way back to text, so it stays in textbox mode.
// Reading through this instead of overwriting showTextbox leaves the stored voice preference intact
// for when the flag is turned back off.
const textboxVisible = computed(() => showTextbox.value || !voiceInputEnabled.value);

const recordingButtonText = computed(() => {
    if (isRecording.value) {
        if (isLgBreakpoint.value) {
            return t("chat.controls.stopRecording");
        }
        return t("chat.controls.stop");
    }

    if (isLgBreakpoint.value) {
        return t("chat.controls.startRecording");
    }
    return t("chat.controls.record");
});

const inputToggleTipText = computed(() => {
    if (isRecording.value) {
        return t("chat.controls.stopRecordingToUseText");
    }

    if (showTextbox.value) {
        return t("chat.controls.useVoice");
    }

    return t("chat.controls.useText");
});

const { show: showBadge, isShowing: isBadgeShowing } = useTimedShow();

const hasFiles = computed(() => useChatStore.files.length > 0);

const shouldShowBadge = computed(() => {
    // Recording is inline in the refresh composer, so only legacy recording uses the badge.
    const showRecordingBadge = isRecording.value && !isNadiaChatRefreshEnabled.value;
    return showRecordingBadge || isBadgeShowing.value || isUploading.value;
});

const isDropzoneVisible = computed(() => isOverBody.value && !props.isDisabled && !isFileUploadLimit.value && props.documentUploadEnabled);

const textPlaceholder = computed(() => {
    // The drop label replaces the prompt while a file is dragged over the page
    if (isDropzoneVisible.value) {
        return "";
    }
    if (props.placeholder) {
        return props.placeholder;
    }
    return hasFiles.value ? t("chat.composer.documentPlaceholder") : t("chat.composer.inputPlaceholder");
});

const statusBadgeText = computed(() => {
    if (isUploading.value) {
        return t("chat.controls.statusBadge.uploadingFiles");
    }

    if (isRecording.value && !isInitializing.value) {
        return t("chat.controls.statusBadge.listening");
    }

    if (showTextbox.value) {
        return t("chat.controls.statusBadge.switchedToText");
    }

    return t("chat.controls.statusBadge.switchedToVoice");
});

const textContent = ref(props.initialValue);
const composerTextarea = useTemplateRef<InstanceType<typeof BaseTextarea>>("composerTextarea");
const textarea = computed(() => composerTextarea.value?.textareaRef);
const composerMentions = ref<ComposerMention[]>([]);
const composerMentionsAvailable = computed(() => !!organizationDetails?.value.is_chat_composer_mentions_enabled && props.composerMentionsEnabled);
const hasInlineMentions = computed(() => composerMentionsAvailable.value && composerMentions.value.length > 0);
// The model holds IME pre-edit text back until it is committed, so the mirror cannot paint it; while
// composing, the textarea shows its own text and the tags go plain for a moment.
const isComposing = ref(false);
useEventListener(textarea, "compositionstart", () => (isComposing.value = true));
useEventListener(textarea, "compositionend", () => (isComposing.value = false));
const paintInlineMentions = computed(() => hasInlineMentions.value && !isComposing.value);
watch(composerMentionsAvailable, (available) => {
    if (!available) {
        composerMentions.value = [];
    }
});
const inputSourceToggle = useTemplateRef<InstanceType<typeof BaseButton>>("inputSourceToggle");
// Lets the page hand focus back to the composer without switching a voice composer to text: the
// message box when it is shown, otherwise the button that switches between voice and text.
defineExpose({
    focus: () => {
        if (textarea.value) {
            textarea.value.focus();
        } else {
            (inputSourceToggle.value?.$el as HTMLButtonElement | undefined)?.focus();
        }
    },
});
watch([() => isRecording.value || isInitializing.value, () => props.isTranscribing], async ([recording, transcribing], [wasRecording, wasTranscribing]) => {
    if (!isNadiaChatRefreshEnabled.value) {
        return;
    }

    await nextTick();
    const activeElement = globalThis.document.activeElement;
    if (activeElement !== null && activeElement !== globalThis.document.body) {
        return;
    }

    if (recording && !wasRecording) {
        unrefElement(refreshRecordingCancelButton)?.focus();
    } else if ((!recording && wasRecording && !transcribing) || (wasTranscribing && !transcribing)) {
        textarea.value?.focus();
    }
});
const refreshTextareaHasMultipleLines = ref(false);
const updateRefreshTextareaLines = () => {
    if (!isNadiaChatRefreshEnabled.value) {
        return;
    }
    void nextTick(() => {
        const field = textarea.value;
        if (!field) {
            return;
        }

        const styles = globalThis.getComputedStyle(field);
        const lineHeight = Number.parseFloat(styles.lineHeight);
        const minHeight = Number.parseFloat(styles.minHeight);
        const verticalPadding = Number.parseFloat(styles.paddingTop) + Number.parseFloat(styles.paddingBottom);
        const singleLineHeight = Math.max(Number.isFinite(lineHeight) ? lineHeight + verticalPadding : 0, Number.isFinite(minHeight) ? minHeight : 0);
        refreshTextareaHasMultipleLines.value = field.scrollHeight > singleLineHeight + 1;
    });
};
watch(textContent, (content) => {
    emit("text-change", content);
    updateRefreshTextareaLines();
});
useResizeObserver(textarea, updateRefreshTextareaLines);

const { piiResult, piiBlocked, piiDetected, piiMessage, piiBannerMessage, acceptRisk, classifyNow, warnSpans, errorSpans } = usePiiDetection(props.piiPatterns, textContent);
const showPiiModal = ref(false);

watch(piiDetected, (detected) => {
    if (detected) {
        const tier = piiBlocked.value ? PII_BLOCK : PII_WARN;
        logUserInteraction(AmplitudeEventNames.PII_WARNING_BAR_SHOWN, { tier, patterns: piiResult.value?.reason }, coachingSessionId);
    }
});

const canSubmit = computed(() => {
    if (props.isDisabled || props.isSending || isUploading.value) {
        return false;
    }
    if (hasFiles.value) {
        // A file attachment alone is enough to send; text is optional
        return true;
    }
    return !!textContent.value.trim();
});

// In the new composer the send button normally grows out of zero width as the mic shrinks. With no mic
// to trade places with it, it holds the mic's resting size so the composer height doesn't jump.
const composerSubmitSizeClass = computed(() => {
    if (!voiceInputEnabled.value) {
        return "h-12 w-12 opacity-100 scale-100";
    }
    return canSubmit.value ? "h-10 w-10 opacity-100 scale-100" : "h-0 w-0 opacity-0 scale-0";
});

const maxLength = computed(() => {
    if (props.textareaMaxLength == undefined) {
        return;
    }
    return parseInt(props.textareaMaxLength);
});

const charCounterVisible = computed(() => {
    if (!maxLength.value || !textContent.value.length) {
        return false;
    }
    return textContent.value.length >= maxLength.value * 0.8; // Show at 80% threshold
});

const charCounterColor = computed(() => {
    if (!maxLength.value) {
        return "text-ink-muted";
    }
    // Warning color at 90%, normal gray below
    return textContent.value.length >= maxLength.value * 0.9 ? "text-notification-warning-text" : "text-ink-muted";
});

const refreshComposerHasExpandedRadius = computed(
    () => composerMentions.value.length > 0 || refreshTextareaHasMultipleLines.value || hasFiles.value || piiBlocked.value || piiDetected.value || charCounterVisible.value,
);

// Fetch file size limits from API (only when uploads are enabled)
const fileSizeLimits = ref<Record<string, number>>({});
const defaultFileSizeLimit = ref(3 * 1024 * 1024); // 3MB default

const { data: fileSizeLimitsData, execute: fetchFileSizeLimits } = useFetch("/api/chat/document/file-size-limits", { immediate: false }).get().json();

if (props.documentUploadEnabled) {
    fetchFileSizeLimits();
}

watch(fileSizeLimitsData, (data) => {
    if (data) {
        fileSizeLimits.value = data.limits || {};
        defaultFileSizeLimit.value = data.default || 3 * 1024 * 1024;
    }
});

watch(showTextbox, (value) => {
    // Save user preference to local storage
    userChatInputPreference.value = value;

    if (value) {
        logUserInteraction(AmplitudeEventNames.TURNED_ON_TEXT_INPUT, {}, coachingSessionUuid);
        recorder.value.releaseStream();

        nextTick(() => {
            textarea.value?.focus();
        });
    } else {
        logUserInteraction(AmplitudeEventNames.TURNED_ON_VOICE_INPUT, {}, coachingSessionUuid);
    }

    showBadge();
});

onMounted(() => {
    updateRefreshTextareaLines();
    logUserInteraction(AmplitudeEventNames.CHAT_INPUT_SHOWN, { mode: textboxVisible.value ? "text" : "voice", location: props.location }, coachingSessionUuid);
    if (props.autoFocusInput && !Capacitor.isNativePlatform()) {
        textarea.value?.focus();
    }
    emitter.on(CHAT_EVENT.DICTATION_EVENT, handleDictationEvent);
    emitter.on(CHAT_EVENT.DICTATION_TEXT, handleDictationTextEvent);
    emitter.on(CHAT_EVENT.USER_MESSAGE_SEND, cancelOutstandingDictationText);
    emitter.on(CHAT_EVENT.STOP_GENERATING, cancelOutstandingDictationText);
    emitter.on(CHAT_EVENT.CANCEL_MESSAGE_DICTATION, cancelMessageDictation);
    emitter.on(CHAT_EVENT.OPEN_TEXT_INPUT, handleTextboxShow);
    emitter.on(CHAT_EVENT.OPEN_VOICE_INPUT, handleVoiceInputShow);
    emitter.on(CHAT_EVENT.SET_MESSAGE_TEXT, handleSetMessageText);
    recorder.value.on("silence", handleRecorderSilence);
    // @ts-expect-error - AudioRecorder.js is not typed, so its instance of mitt is not typed
    recorder.value.on("level", handleRecorderLevel);
    recorder.value.on("start", handleRecorderStart);
    recorder.value.on("stop", handleRecorderStopped);
    // @ts-expect-error - AudioRecorder.js is not typed, so its instance of mitt is not typed
    recorder.value.on("finish", handleRecorderFinished);
    recorder.value.on("initializing", handleInitializing);
    recorder.value.on("initialized", handleInitialized);
    recorder.value.on("initialization-failed", handleInitializationFailed);

    audioPlayer.value?.addEventListener("ended", handleAudioFinished);
    fileInput.value?.addEventListener("cancel", logUploadCanceled);
    document.addEventListener("visibilitychange", handleVisibilityChange);

    setTimeout(() => {
        hasSeenDocumentUploadFeatureTooltip.value = true;
        isDocumentUploadTooltipShown.value = false;
    }, 10000);
});

watch(
    () => props.uploadContextKey,
    () => {
        uploadGeneration += 1;
        resetDocumentIntake();
    },
);
onUnmounted(() => {
    uploadOwnerActive = false;
    uploadGeneration += 1;
    componentActive = false;
    readerFallbackGeneration.value += 1;
    emitter.off(CHAT_EVENT.DICTATION_EVENT, handleDictationEvent);
    emitter.off(CHAT_EVENT.DICTATION_TEXT, handleDictationTextEvent);
    emitter.off(CHAT_EVENT.USER_MESSAGE_SEND, cancelOutstandingDictationText);
    emitter.off(CHAT_EVENT.STOP_GENERATING, cancelOutstandingDictationText);
    emitter.off(CHAT_EVENT.CANCEL_MESSAGE_DICTATION, cancelMessageDictation);
    abortAllDictationTextRequests();
    emitter.off(CHAT_EVENT.OPEN_TEXT_INPUT, handleTextboxShow);
    emitter.off(CHAT_EVENT.OPEN_VOICE_INPUT, handleVoiceInputShow);
    emitter.off(CHAT_EVENT.SET_MESSAGE_TEXT, handleSetMessageText);

    clearTimeout(idleMicReleaseTimeout);
    stopRecordingTimer();
    if (isRecording.value) {
        recorder.value.stop({ abort: true });
    }
    recorder.value.releaseStream();

    // Clean up component-specific listeners
    recorder.value.off("silence", handleRecorderSilence);
    // @ts-expect-error - AudioRecorder.js is not typed, so its instance of mitt is not typed
    recorder.value.off("level", handleRecorderLevel);
    recorder.value.off("start", handleRecorderStart);
    recorder.value.off("stop", handleRecorderStopped);
    // @ts-expect-error - AudioRecorder.js is not typed, so its instance of mitt is not typed
    recorder.value.off("finish", handleRecorderFinished);
    recorder.value.off("initializing", handleInitializing);
    recorder.value.off("initialized", handleInitialized);
    recorder.value.off("initialization-failed", handleInitializationFailed);

    // Clean up blob URLs to prevent memory leaks
    if (audioPlayer.value?.src) {
        URL.revokeObjectURL(audioPlayer.value.src);
    }

    audioPlayer.value?.removeEventListener("ended", handleAudioFinished);
    fileInput.value?.removeEventListener("cancel", logUploadCanceled);
    document.removeEventListener("visibilitychange", handleVisibilityChange);

    useChatStore.removeAllFiles();
});

const getNextDictationChunk = () => {
    if (!useChatStore.dictationOn || isRecording.value) {
        return null;
    }
    if (!nextDictationChunkId.value.messageId || !nextDictationChunkId.value.sequenceNumber) {
        return null;
    }
    const messageChunks = dictationChunks.value[nextDictationChunkId.value.messageId];
    return messageChunks?.find((c) => c.sequenceNumber === nextDictationChunkId.value.sequenceNumber);
};

const deleteDictationChunk = (messageId: string, sequenceNumber: number) => {
    if (!dictationChunks.value[messageId]) {
        return;
    }
    dictationChunks.value[messageId] = dictationChunks.value[messageId].filter((c) => c.sequenceNumber !== sequenceNumber);
};

const advancePastFailedDictationSequences = () => {
    const messageId = nextDictationChunkId.value.messageId;
    let sequenceNumber = nextDictationChunkId.value.sequenceNumber;
    const failedSequences = messageId ? failedDictationSequences.get(messageId) : undefined;
    while (sequenceNumber && failedSequences?.has(sequenceNumber)) {
        failedSequences.delete(sequenceNumber);
        sequenceNumber += 1;
    }
    if (failedSequences?.size === 0 && messageId) {
        failedDictationSequences.delete(messageId);
    }
    if (messageId && sequenceNumber !== nextDictationChunkId.value.sequenceNumber) {
        nextDictationChunkId.value = { messageId, sequenceNumber };
    }
};

const handleAudioFinished = () => {
    advancePastFailedDictationSequences();
    const chunk = getNextDictationChunk();
    if (!chunk) {
        if (isDictationPlaying.value) {
            logUserInteraction(AmplitudeEventNames.COACH_DICTATION_STOP, {}, coachingSessionUuid);
        }
        isDictationPlaying.value = false;
        useChatStore.setDictationPlayingMessageId(null);
        return;
    }

    // Queue up the chunk to look for after this one finishes, if any
    nextDictationChunkId.value = { messageId: chunk.messageId, sequenceNumber: (chunk.sequenceNumber ?? 0) + 1 };

    // Create a URL for the Blob and set it as the src of the audio element
    // Revoke old blob URL to prevent memory leak from accumulating blob references
    if (audioPlayer.value?.src && chunk.blob) {
        URL.revokeObjectURL(audioPlayer.value.src);
        audioPlayer.value.src = URL.createObjectURL(chunk.blob);
    }

    // Now that the blob is on the audio player, we can delete this chunk to free up memory
    if (chunk.messageId && chunk.sequenceNumber) {
        deleteDictationChunk(chunk.messageId, chunk.sequenceNumber);
    }

    applyDictationSpeed();

    audioPlayer.value
        ?.play()
        ?.then(() => {
            // Autoplay started.
            if (!isDictationPlaying.value) {
                logUserInteraction(AmplitudeEventNames.COACH_DICTATION_START, {}, coachingSessionUuid);
            }
            isDictationPlaying.value = true;
            useChatStore.setDictationPlayingMessageId(chunk.messageId ?? null);
        })
        .catch(() => {
            // Autoplay was prevented because the user hasn't take an action.
            // TODO: consider showing a "play" button so that user can start playback.
            isDictationPlaying.value = false;
            useChatStore.setDictationPlayingMessageId(null);
            logUserInteraction(AmplitudeEventNames.COACH_DICTATION_PREVENTED, {}, coachingSessionUuid);
        });
};

const convertB64AudioToBlob = (audioStream: string, audioFormat: "mp3" | "wav" = "mp3") => {
    const byteCharacters = atob(audioStream);
    const byteNumbers = new Array(byteCharacters.length);
    for (let i = 0; i < byteCharacters.length; i++) {
        byteNumbers[i] = byteCharacters.charCodeAt(i);
    }
    const byteArray = new Uint8Array(byteNumbers);
    return new Blob([byteArray], { type: audioFormat === "wav" ? "audio/wav" : "audio/mp3" });
};

const handleDictationEvent = (data: { data: { message_id: string; dictation_count: number; audio_stream: string; audio_format?: "mp3" | "wav" } }) => {
    if (!useChatStore.dictationOn) {
        // To save memory, don't bother storing dictation chunks if the user is not using dictation
        return;
    }

    const messageId = data.data.message_id;

    if (skippedLegacyDictationMessageIds.has(messageId)) {
        return;
    }
    const sequenceNumber = data.data.dictation_count;
    const blob = convertB64AudioToBlob(data.data.audio_stream, data.data.audio_format);
    const chunk: DictationChunk = { messageId, sequenceNumber, blob };

    if (dictationChunks.value[messageId]) {
        dictationChunks.value[messageId].push(chunk);
    } else {
        dictationChunks.value[messageId] = [chunk];
    }

    if (sequenceNumber === 1) {
        // This is the first chunk of what is presumably the newest/latest message, so cancel any
        // currently playing dictations and queue up this one.
        stopDictation();
        nextDictationChunkId.value = { messageId, sequenceNumber };
    }

    if (!isDictationPlaying.value && messageId === nextDictationChunkId.value.messageId && sequenceNumber === nextDictationChunkId.value.sequenceNumber) {
        // Either this is the first chunk of a new message, or we had paused the audio while waiting
        // on this chunk, so start it immediately.
        handleAudioFinished(); // Starts the new chunk
    }
};

const waitForDictationRetry = (signal: AbortSignal, delay: number) =>
    new Promise<boolean>((resolve) => {
        if (signal.aborted) {
            resolve(false);
            return;
        }
        let timeout: ReturnType<typeof setTimeout>;
        const abort = () => {
            clearTimeout(timeout);
            resolve(false);
        };
        timeout = setTimeout(() => {
            signal.removeEventListener("abort", abort);
            resolve(true);
        }, delay);
        signal.addEventListener("abort", abort, { once: true });
    });

const processDictationTextRequests = () => {
    while (activeDictationTextRequests < MAX_ACTIVE_DICTATION_TEXT_REQUESTS && pendingDictationTextRequests.length > 0) {
        const request = pendingDictationTextRequests.shift();
        if (!request || request.controller.signal.aborted) {
            continue;
        }

        activeDictationTextRequests += 1;
        const { data, controller, retries } = request;
        const body: DictationRequest = {
            text: data.content,
            signature: data.signature,
            coaching_session_uuid: data.coaching_session_uuid,
            message_id: data.message_id,
            dictation_count: data.dictation_count,
        };
        let rescheduled = false;
        let holdsConcurrencySlot = true;
        void (async () => {
            try {
                const response = await fetchApi("/v1/chat/dictation", {
                    method: "POST",
                    body: JSON.stringify(body),
                    signal: controller.signal,
                });
                if (response.status === 429 && retries < 2 && !controller.signal.aborted) {
                    activeDictationTextRequests -= 1;
                    holdsConcurrencySlot = false;
                    processDictationTextRequests();
                    if ((await waitForDictationRetry(controller.signal, [500, 1000][retries])) && !controller.signal.aborted) {
                        const retry = { ...request, retries: retries + 1 };
                        const firstRequestForMessage = pendingDictationTextRequests.findIndex((item) => item.data.message_id === data.message_id);
                        if (firstRequestForMessage === -1) {
                            pendingDictationTextRequests.push(retry);
                        } else {
                            let insertAt = firstRequestForMessage;
                            while (
                                insertAt < pendingDictationTextRequests.length &&
                                pendingDictationTextRequests[insertAt].data.message_id === data.message_id &&
                                pendingDictationTextRequests[insertAt].data.dictation_count <= data.dictation_count
                            ) {
                                insertAt += 1;
                            }
                            pendingDictationTextRequests.splice(insertAt, 0, retry);
                        }
                        rescheduled = true;
                    }
                    return;
                }
                if (!response.ok) {
                    throw new Error("Dictation audio request failed");
                }
                const audio = await response.blob();
                if (controller.signal.aborted || skippedMessageIds.has(data.message_id) || !useChatStore.dictationOn || !componentActive) {
                    return;
                }
                const chunk: DictationChunk = {
                    messageId: data.message_id,
                    sequenceNumber: data.dictation_count,
                    blob: new Blob([audio], { type: "audio/wav" }),
                };
                if (dictationChunks.value[data.message_id]) {
                    dictationChunks.value[data.message_id].push(chunk);
                } else {
                    dictationChunks.value[data.message_id] = [chunk];
                }
                if (!isDictationPlaying.value && data.message_id === nextDictationChunkId.value.messageId && data.dictation_count === nextDictationChunkId.value.sequenceNumber) {
                    handleAudioFinished();
                }
            } catch {
                if (controller.signal.aborted || skippedMessageIds.has(data.message_id) || !componentActive) {
                    return;
                }
                const failedSequences = failedDictationSequences.get(data.message_id) ?? new Set<number>();
                failedSequences.add(data.dictation_count);
                failedDictationSequences.set(data.message_id, failedSequences);
                if (!isDictationPlaying.value && data.message_id === nextDictationChunkId.value.messageId && data.dictation_count === nextDictationChunkId.value.sequenceNumber) {
                    handleAudioFinished();
                }
            } finally {
                if (holdsConcurrencySlot) {
                    activeDictationTextRequests -= 1;
                }
                if (!rescheduled) {
                    removeDictationTextController(data.message_id, controller);
                }
                processDictationTextRequests();
            }
        })();
    }
};

const handleDictationTextEvent = (event: {
    data: {
        message_id: string;
        dictation_count: number;
        coaching_session_uuid: string;
        content: string;
        signature: string;
    };
}) => {
    const { data } = event;
    if (!useChatStore.dictationOn) {
        if (skippedMessageIds.has(data.message_id)) {
            return;
        }
        if (data.dictation_count === 1) {
            clearFailedDictationSequencesForOtherMessages(data.message_id);
            nextDictationChunkId.value = { messageId: data.message_id, sequenceNumber: 1 };
        }
        const failedSequences = failedDictationSequences.get(data.message_id) ?? new Set<number>();
        failedSequences.add(data.dictation_count);
        failedDictationSequences.set(data.message_id, failedSequences);
        return;
    }
    if (skippedMessageIds.has(data.message_id)) {
        return;
    }

    if (data.dictation_count === 1) {
        abortAllDictationTextRequests(data.message_id);
        clearFailedDictationSequencesForOtherMessages(data.message_id);
        stopDictation();
        nextDictationChunkId.value = { messageId: data.message_id, sequenceNumber: 1 };
    }

    const controller = new AbortController();
    const controllers = dictationTextControllers.get(data.message_id) ?? new Map<AbortController, number>();
    controllers.set(controller, data.dictation_count);
    dictationTextControllers.set(data.message_id, controllers);
    const firstRequestForMessage = pendingDictationTextRequests.findIndex((request) => request.data.message_id === data.message_id);
    if (firstRequestForMessage === -1) {
        pendingDictationTextRequests.push({ data, controller, retries: 0 });
    } else {
        let insertAt = firstRequestForMessage;
        while (
            insertAt < pendingDictationTextRequests.length &&
            pendingDictationTextRequests[insertAt].data.message_id === data.message_id &&
            pendingDictationTextRequests[insertAt].data.dictation_count <= data.dictation_count
        ) {
            insertAt += 1;
        }
        pendingDictationTextRequests.splice(insertAt, 0, { data, controller, retries: 0 });
    }
    processDictationTextRequests();
};

const handleTextboxShow = () => {
    if (props.isDisabled) {
        return;
    }

    showTextbox.value = true;
    emit("textbox-show");
    nextTick(() => {
        textarea.value?.focus();
    });
};

const handleVoiceInputShow = () => {
    if (props.isDisabled || !voiceInputEnabled.value) {
        return;
    }

    showTextbox.value = false;
    handleRecordingStart();
};

const handleSetMessageText = (draft: string | { content: string; mentions: ComposerMention[] }) => {
    textContent.value = typeof draft === "string" ? draft : draft.content;
    composerMentions.value = [];
    showTextbox.value = true;
    nextTick(() => {
        if (typeof draft !== "string" && composerMentionsAvailable.value && textContent.value === draft.content) {
            composerMentions.value = draft.mentions;
        }
        textarea.value?.focus();
    });
};

const draftFiles = computed<ChatInputFile[]>(() => useChatStore.files);
const isFileUploadLimit = computed(() => props.isFileUploadLimit || draftFiles.value.length >= FILE_UPLOAD_LIMIT);
const removeAttachment = (file: ChatInputFile) => {
    useChatStore.removeAttachment(file);
};

const doSend = () => {
    const clean = DOMPurify.sanitize(textContent.value, { FORBID_TAGS: ["a"] });
    emit("text-send", {
        content: clean,
        files: [...useChatStore.files],
        ...(composerMentionsAvailable.value && composerMentions.value.length ? { mentions: reconcileMentions(textContent.value, clean, composerMentions.value) } : {}),
    });
    // Conversation starters keep the draft for retry until navigation succeeds.
    if (props.preserveDraftOnSend || props.location === CHAT_MESSAGE_CONTROLS_LOCATION.HOMEPAGE) {
        return;
    }
    useChatStore.removeAllFiles();
    textContent.value = "";
};

const handleSendMessage = (event: Event) => {
    // Not unlessComposing: Shift+Enter must keep its default newline, so preventDefault stays conditional below.
    if (event instanceof KeyboardEvent && isImeComposing(event)) {
        return;
    }
    if (event instanceof KeyboardEvent && !event.shiftKey) {
        event.preventDefault();
    }

    if (!canSubmit.value) {
        return;
    }

    const isValidTrigger = (event instanceof KeyboardEvent && !event.shiftKey) || !(event instanceof KeyboardEvent);
    if (!isValidTrigger) {
        return;
    }

    if (props.validatePiiOnSend) {
        classifyNow();
    }
    if (piiBlocked.value) {
        showPiiModal.value = true;
        logUserInteraction(AmplitudeEventNames.PII_BLOCKED_MODAL_SHOWN, { patterns: piiResult.value?.reason }, coachingSessionId);
        return;
    }

    if (piiDetected.value) {
        showPiiModal.value = true;
        logUserInteraction(AmplitudeEventNames.PII_WARNING_MODAL_SHOWN, { patterns: piiResult.value?.reason }, coachingSessionId);
        return;
    }

    doSend();
};

const handlePiiModalConfirm = () => {
    logUserInteraction(AmplitudeEventNames.PII_WARNING_MODAL_SEND_ANYWAY, { patterns: piiResult.value?.reason }, coachingSessionId);
    acceptRisk();
    doSend();
};

const handlePiiEdited = (tier: number) => {
    logUserInteraction(AmplitudeEventNames.PII_WARNING_EDITED, { tier, patterns: piiResult.value?.reason }, coachingSessionId);
};

const handleKeyDown = () => emit("text-keydown");

const handleRecordingCancel = () => {
    recorder.value.stop({ abort: true });
    recorder.value.releaseStream();
};
const handleRecorderSilence = () => {
    handleRecordingCancel();
    openTestDialog();
};
const MAX_LEVEL_HISTORY = 100;

const handleRecorderLevel = (level: number) => {
    const history = recordingAudioLevelHistory.value;
    if (history.length >= MAX_LEVEL_HISTORY) {
        history.shift();
    }
    history.push(level);
};
const handleInitializing = () => (isInitializing.value = true);
const handleInitialized = () => (isInitializing.value = false);
const handleRecorderStart = () => {
    if (recordClickTime != null) {
        initToStartMs = Math.round(performance.now() - recordClickTime);
    }
    startRecordingTimer();
};
const handleInitializationFailed = () => (isInitializing.value = false);
const handleRecorderStopped = () => {
    stopRecordingTimer();
    recordingAudioLevelHistory.value = [];
    isRecording.value = false;
    logUserInteraction(AmplitudeEventNames.USER_RECORDING_ENDED, {}, coachingSessionUuid);

    clearTimeout(idleMicReleaseTimeout);
    idleMicReleaseTimeout = setTimeout(() => recorder.value.releaseStream(), IDLE_MIC_RELEASE_MS);
};
const handleVisibilityChange = () => {
    if (document.hidden && !isRecording.value) {
        recorder.value.releaseStream();
    }
};
const handleRecorderFinished = ({ blob, lengthMs, audioStats }: { blob: Blob; lengthMs: number; audioStats: AudioStats }) => {
    emit("recording-complete", {
        blob,
        recordingTime: msToDisplayTime(lengthMs),
        audioStats: {
            ...audioStats,
            ...(initToStartMs != null && { initToStartMs }),
        },
    });
    recordClickTime = null;
    initToStartMs = null;
};
const handleRecordingStart = async () => {
    // The mic affordances are unrendered when voice input is hidden, but the voice nudge and the
    // mic-permission dialog can still emit their way here.
    if (props.isDisabled || !voiceInputEnabled.value) {
        return;
    }

    if (micConsentBlocks(handleRecordingStart)) {
        return;
    }

    clearTimeout(idleMicReleaseTimeout);
    let promptOpened = false;
    recordClickTime = performance.now();
    initToStartMs = null;
    logUserInteraction(AmplitudeEventNames.USER_RECORDING_START, {}, coachingSessionUuid);

    stopDictation();

    isRecording.value = true;

    try {
        await recorder.value.start((micPermissionState: PermissionState) => {
            if (micPermissionState === "prompt") {
                promptOpened = true;
                showMicPermissionAide.value = true;
            }
        });

        // If the tab became hidden or the component unmounted while waiting
        // for getUserMedia, abort the recording and release the mic.
        if (document.hidden || !componentActive) {
            recorder.value.stop({ abort: true });
            isRecording.value = false;
            recorder.value.releaseStream();
            return;
        }

        /*
         * User may have chosen "I'd rather type" in the permission dialog
         * but still grant permission in the browser prompt. In this case,
         * we should not continue with a recording because the user
         * chose to use text.
         */
        if (promptOpened && showTextbox.value) {
            recorder.value.stop({ abort: true });
            isRecording.value = false;
            return;
        }
        emit("recording-start");
    } catch (e) {
        isRecording.value = false;

        if (e instanceof Error) {
            switch (e.name) {
                case "NotAllowedError":
                case "NotSupportedError":
                case "OverconstrainedError":
                case "NotFoundError":
                    openTestDialog();
                    break;
                default:
                    emit("recording-error");
            }
        }

        logError(e);
        logUserInteraction(AmplitudeEventNames.MIC_ERROR, props.userCoachExchangeId ? { uce_id: props.userCoachExchangeId } : {}, coachingSessionUuid);
    } finally {
        if (showMicPermissionAide.value) {
            showMicPermissionAide.value = false;
        }
    }
};

const openTestDialog = () => emitter.emit(CHAT_EVENT.OPEN_MIC_TEST_DIALOG);
const stopDictation = () => {
    if (audioPlayer.value) {
        audioPlayer.value.src = "";
        audioPlayer.value.pause();
    }
    isDictationPlaying.value = false;
    nextDictationChunkId.value = { messageId: undefined };
    useChatStore.setDictationPlayingMessageId(null);
};

watch(
    () => useChatStore.dictationOn,
    (dictationOn) => {
        if (!dictationOn) {
            markDictationTextRequestsAsFailed();
            abortAllDictationTextRequests();
        }
        if (!dictationOn && isNadiaChatRefreshEnabled.value) {
            stopDictation();
        }
        if (dictationOn) {
            advancePastFailedDictationSequences();
            if (!isDictationPlaying.value) {
                handleAudioFinished();
            }
        }
    },
);

const cycleDictationSpeed = () => {
    useChatStore.cycleDictationSpeed();
    logUserInteraction(AmplitudeEventNames.COACH_DICTATION_SPEED_CHANGED, { speed: useChatStore.dictationSpeed }, coachingSessionUuid);
};

const cancelMessageDictation = (messageId: string) => {
    abortDictationTextRequests(messageId);
    failedDictationSequences.delete(messageId);
    skippedMessageIds.add(messageId);
    skippedLegacyDictationMessageIds.add(messageId);
    delete dictationChunks.value[messageId];
    if (nextDictationChunkId.value.messageId === messageId) {
        stopDictation();
    }
};

const skipDictation = () => {
    const currentMessageId = nextDictationChunkId.value.messageId;
    const currentSequenceNumber = nextDictationChunkId.value.sequenceNumber;
    stopDictation();
    if (currentMessageId) {
        abortDictationTextRequests(currentMessageId);
        failedDictationSequences.delete(currentMessageId);
        skippedMessageIds.add(currentMessageId);
        skippedLegacyDictationMessageIds.add(currentMessageId);
        delete dictationChunks.value[currentMessageId];
    }
    logUserInteraction(
        AmplitudeEventNames.COACH_DICTATION_SKIPPED,
        { message_id: currentMessageId, sequence_number: currentSequenceNumber, speed: useChatStore.dictationSpeed },
        coachingSessionUuid,
    );
};
const handleMicSelection = (event: MediaDeviceInfo) => recorder.value.setAudioInputDeviceId(event.deviceId);
// NOTE: For the future "File Uploads" feature see `chat.actions.file_upload`
// 1. UI Component here sends a `file_upload` action code (see `transcription_request` for an example) and gets a `signed_url` in response.
// 2. Client then uploads the file to the `signed_url` and gets the `file_url` or `object_id` in response.
// 3. Once the file is uploaded, the client holds this state and sends a `file_uploaded/object_id` property along with the message when clicking "Send".
const handlePaste = (event: ClipboardEvent) => {
    if (props.isPasteDisabled) {
        return;
    }

    // Office apps often supply a rendered image alongside copied text. Preserve text paste in that case.
    if (!props.isDisabled && !isFileUploadLimit.value && props.visualDocumentAnalysisEnabled && !event.clipboardData?.getData("text/plain")) {
        const pastedFiles = [...(event.clipboardData?.items ?? [])]
            .filter((entry) => entry.kind === "file")
            .map((entry) => {
                const file = entry.getAsFile();
                if (!file) {
                    return null;
                }
                if (file.type.startsWith("image/")) {
                    const extension = file.type.split("/")[1];
                    const stamp = new Date().toISOString().replace(/[:.]/g, "-");
                    return new File([file], `pasted-screenshot-${stamp}.${extension}`, { type: file.type });
                }
                return file;
            })
            .filter((file): file is File => file !== null);
        if (pastedFiles.length) {
            event.preventDefault();
            void routeFiles(pastedFiles, "paste");
            return;
        }
    }

    if ((event.clipboardData?.getData("text/plain")?.length ?? 0) > DOCUMENT_LENGTH_THRESHOLD) {
        event.preventDefault();
        const NUM_BYTES = 100;
        const now = new Date();
        const content = event.clipboardData?.getData("text/plain") ?? "";
        const file = {
            name: `pasted-content-${now.toISOString()}.txt`,
            content: content,
            description: content.slice(0, NUM_BYTES).replace(/\n/g, " ") + "...",
        };
        useChatStore.addFile(file);
        emit("text-paste", file);
    }
};

function handleMicPermissionDialogClick({ useText } = { useText: true }) {
    showMicPermissionAide.value = false;
    showTextbox.value = useText;
}

function handleDictationToggle() {
    logUserInteraction(useChatStore.dictationOn ? "turned_dictation_off" : "turned_dictation_on", {}, coachingSessionUuid);
    useChatStore.toggleDictation();
}

// Document Uploader
const fileInput = useTemplateRef("fileInput");

const handleFileChange = async (event: Event) => {
    const target = event.target as HTMLInputElement;
    try {
        await routeFiles([...(target.files ?? [])], "upload_button");
    } finally {
        target.value = "";
    }
};

// Send and the upload controls stay disabled until every in-flight batch settles — a
// per-batch boolean lets an early finisher re-enable them while another upload is pending.
const pendingUploads = ref(0);
const isUploading = computed(() => pendingUploads.value > 0);
const uploadErrors = ref<string[]>([]);

const emitUploadError = () => {
    emit("upload-error", uploadErrors.value.join("\n"));
};

const applyUploadResults = (results: PromiseSettledResult<Awaited<ReturnType<typeof uploadFile>>>[], filesForUpload: File[], isStale: () => boolean) => {
    const uploaded: { file: File; value: unknown }[] = [];
    results.forEach((resp, index) => {
        if (isStale()) {
            return;
        }
        const file = filesForUpload[index]!;
        const errorPrefix = "An error occurred while uploading file";
        const genericFileUploadError = t("chat.controls.uploadErrors.uploadFailed", { file: file.name });
        if (resp.status === "rejected") {
            if (isSessionEndedError(resp.reason)) {
                return;
            }
            if (resp.reason?.cause === WAF_ERROR_CAUSE) {
                uploadErrors.value.push(t("chat.controls.uploadErrors.blockedBySecurity", { supportUrl: $feedbackMailto }));
            } else {
                uploadErrors.value.push(resp.reason?.cause ? resp.reason.message : genericFileUploadError);
            }
            logUserInteraction("upload_errored", { error_prefix: errorPrefix, error_message: resp.reason, file_type: file.type }, coachingSessionUuid);
            return;
        }
        if (resp.value.error) {
            uploadErrors.value.push(genericFileUploadError);
            logUserInteraction("upload_errored", { error_prefix: errorPrefix, error_message: resp.value.error, file_type: file.type }, coachingSessionUuid);
            return;
        }
        useChatStore.addFile(resp.value);
        uploaded.push({ file, value: resp.value });
        if (resp.value.truncated) {
            const pct = Math.round(resp.value.truncation_ratio * 100);
            uploadErrors.value.push(t("chat.controls.uploadErrors.truncated", { name: resp.value.name, percent: pct }));
        }
        logUserInteraction(
            AmplitudeEventNames.UPLOAD_FINISHED,
            {
                file_size: file.size,
                file_type: file.type,
            },
            coachingSessionUuid,
        );
    });
    return uploaded;
};

const handleUpload = async (files: File[], source: string, extraFields: Record<string, string> = {}, isStale: () => boolean = () => false) => {
    if (props.isDisabled || isFileUploadLimit.value) {
        return [];
    }
    const generation = uploadGeneration;
    const uploadIsStale = () => !uploadOwnerActive || generation !== uploadGeneration || isStale();
    pendingUploads.value += 1;
    emit("upload-error", null);
    uploadErrors.value = [];

    let filesForUpload = normalizeMarkdownFiles(files, ALL_SUPPORTED_FILE_TYPES);

    let SUPPORTED_FILE_TYPES = [...DOCUMENT_TYPES];
    if (organizationDetails?.value.is_image_upload_enabled) {
        SUPPORTED_FILE_TYPES.push(...IMAGE_TYPES);
    }

    const unsupportedFiles = filesForUpload.filter((file) => !SUPPORTED_FILE_TYPES.includes(file.type));
    if (unsupportedFiles.length > 0) {
        const errorPrefix = "Unsupported file type";
        const errorMsg = `${errorPrefix}: ${unsupportedFiles.map((file) => file.name).join(", ")}`;
        uploadErrors.value.push(t("chat.controls.uploadErrors.unsupportedType", { files: unsupportedFiles.map((file) => file.name).join(", ") }));
        unsupportedFiles.forEach((file) => {
            logUserInteraction("upload_errored", { error_prefix: errorPrefix, error_message: errorMsg, file_size: file.size, file_type: file.type }, coachingSessionUuid);
        });
        filesForUpload = filesForUpload.filter((file) => !unsupportedFiles.includes(file));
    }

    // Use file size limits from API
    const oversizedFiles = filesForUpload.filter((file) => {
        const limit = fileSizeLimits.value[file.type] || defaultFileSizeLimit.value;
        return file.size > limit;
    });

    if (oversizedFiles.length > 0) {
        oversizedFiles.forEach((file) => {
            const limit = fileSizeLimits.value[file.type] || defaultFileSizeLimit.value;
            const limitMB = Math.floor(limit / (1024 * 1024));
            const errorPrefix = `File size exceeds ${limitMB} MB limit`;
            const errorMsg = `${errorPrefix}: ${file.name}`;
            uploadErrors.value.push(t("chat.controls.uploadErrors.fileTooLarge", { limit: limitMB, file: file.name }));
            logUserInteraction("upload_errored", { error_prefix: errorPrefix, error_message: errorMsg, file_size: file.size, file_type: file.type }, coachingSessionUuid);
        });
        filesForUpload = filesForUpload.filter((file) => {
            const limit = fileSizeLimits.value[file.type] || defaultFileSizeLimit.value;
            return file.size <= limit;
        });
    }

    if (filesForUpload.length === 0) {
        emitUploadError();
        pendingUploads.value -= 1;
        return [];
    }

    filesForUpload.forEach((file) => {
        logUserInteraction(
            "upload_started",
            {
                file_size: file.size,
                file_type: file.type,
                message_count: props.userMessageCount,
                source,
            },
            coachingSessionUuid,
        );
    });

    const results = await Promise.allSettled(filesForUpload.map((file) => uploadFile(file, extraFields)));
    const uploaded = applyUploadResults(results, filesForUpload, uploadIsStale);
    if (uploadErrors.value.length > 0) {
        emitUploadError();
    }
    pendingUploads.value -= 1;
    return uploaded;
};

const handleFileUploadClick = () => {
    logUserInteraction(AmplitudeEventNames.UPLOAD_BUTTON_CLICKED, {}, coachingSessionUuid);
    fileInput.value?.click();
};

const logUploadCanceled = () => {
    logUserInteraction(AmplitudeEventNames.UPLOAD_CANCELED, {}, coachingSessionUuid);
};

const throwUploadHttpError = async (response: Response): Promise<never> => {
    if (response.status === 403) {
        // A 403 the refresh could not save is usually a genuine block (e.g. WAF), but it
        // is a dead or stale session when the body carries one of our codes -- in which
        // case the dialog is already saying so and this must not add a second, wrongly
        // worded message beside it (ENG-1667).
        let body: unknown = null;
        try {
            body = await response.json();
        } catch {
            body = null;
        }
        const sessionEndedCode = sessionEndedCodeFrom(body);
        if (sessionEndedCode) {
            // Reported here as a backstop: the wrapper skips both the retry and the
            // dialog when the token refresh itself is unavailable, and the results
            // handler below discards this error on the assumption the dialog is
            // showing. First report wins, so when the wrapper did report, this is a
            // no-op. Mirrors StreamingPostClient._handleHttpError.
            reportSessionEnded(sessionEndedCode, (body as { login_url?: string }).login_url ?? null);
            throw new Error("Session ended", { cause: SESSION_ENDED_ERROR_CAUSE });
        }
        throw new Error(`Your file upload was blocked by our security system. Please [contact support](${$feedbackMailto}) if you believe this is an error.`, {
            cause: WAF_ERROR_CAUSE,
        });
    }
    // Surface server error messages (e.g. PII rejection, file validation) instead of generic status text
    if (response.status === 400) {
        const errorData = await response.json().catch(() => null);
        if (errorData?.error) {
            throw new Error(errorData.error, { cause: "server_error" });
        }
    }
    throw new Error(`Upload failed with status ${response.status}: ${response.statusText}`);
};

const uploadFile = async (file: File, extraFields: Record<string, string> = {}) => {
    const formData = new FormData();
    formData.append("file", file);
    if (coachingSessionUuid) {
        formData.append("coaching_session_uuid", coachingSessionUuid);
    }
    for (const [key, value] of Object.entries(extraFields)) {
        formData.append(key, value);
    }

    const postUpload = (csrfToken: string) =>
        fetch("/api/chat/document/upload", {
            method: "POST",
            body: formData,
            headers: withRequestId({
                "X-CSRFToken": csrfToken,
            }),
        });

    try {
        let response = await postUpload(getCookie("csrftoken") ?? "");

        // The org blocks access (revoked encryption, IP allowlist): retrying with a fresh token
        // cannot help, and it is not a WAF block. The reloaded page explains it.
        if (isAccessBlocked(response)) {
            reloadWhenAccessBlocked(response);
            const body = await response.json().catch(() => null);
            throw new Error(body?.error ?? `Upload failed with status ${response.status}: ${response.statusText}`, { cause: "server_error" });
        }

        // Embedded webviews (MS Teams) can hold a stale csrftoken cookie that 403s every
        // POST. Fetch a server-issued token and retry once before surfacing the failure —
        // mirrors StreamingPostClient's CSRF recovery for the SSE path. Stands down when
        // the 403 carries a session-ended code, because then the global fetch wrapper
        // already retried and (on failure) reported the dialog.
        if (response.status === 403 && !(await wasGloballyHandled403(response))) {
            const freshToken = await refreshCsrfToken();
            if (freshToken) {
                response = await postUpload(freshToken);
            }
        }

        if (!response.ok) {
            await throwUploadHttpError(response);
        }

        const result = await response.json();
        return result;
    } catch (error) {
        console.error(`Upload failed for ${file.name}:`, error);
        throw error;
    }
};

const isMobileApp = usePage().props?.is_mobile_app;
const isDocumentUploadTooltipShown = ref(!isMobileApp && !hasSeenDocumentUploadFeatureTooltip.value);
const acceptedFileTypes = computed(() => getAcceptedFileTypes(!!organizationDetails?.value.is_image_upload_enabled));

// One picker covers every route: legacy-supported formats plus the reader's PDF/image types.
const unifiedAcceptedFileTypes = computed(() => {
    const types = new Set(acceptedFileTypes.value.split(","));
    if (props.visualDocumentAnalysisEnabled) {
        READER_FILE_EXTENSIONS.forEach((extension) => types.add(extension));
    }
    return [...types].join(",");
});

const uploadFileAriaLabel = computed(() => (props.visualDocumentAnalysisEnabled ? t("chat.composer.uploadFileWithReader") : t("chat.composer.uploadFile")));

const documentUploadTooltip = computed(() => {
    const displayTypes = formatAcceptedFileTypes(unifiedAcceptedFileTypes.value);
    if (!hasSeenDocumentUploadFeatureTooltip.value) {
        return t("chat.composer.uploadTooltipIntro", { fileTypes: displayTypes });
    }
    if (isFileUploadLimit.value) {
        return t("chat.composer.uploadLimitReached", { limit: FILE_UPLOAD_LIMIT });
    }
    if (isUploading.value) {
        return t("chat.composer.uploadingDocument");
    }
    return props.visualDocumentAnalysisEnabled
        ? t("chat.composer.uploadFileTypesWithReader", { fileTypes: displayTypes })
        : t("chat.composer.uploadFileTypes", { fileTypes: displayTypes });
});
watch(isDocumentUploadTooltipShown, (newValue, oldValue) => {
    if (!newValue && newValue !== oldValue) {
        hasSeenDocumentUploadFeatureTooltip.value = true;
    }
});
</script>

<style scoped>
@reference "../../../css/tailwind.css";
[autocomplete="off"] div[data-lastpass-icon-root="true"] {
    display: none;
}

[autocomplete="off"] div[data-lastpass-infield="true"] {
    display: none;
}

textarea {
    resize: none;
}

/* ComposerMentionHighlights paints the text, tags included, underneath; the caret stays the textarea's. */
.composer-textarea-mentioned {
    @apply text-transparent caret-ink;
}

/* Spec: one 24px input line snug under the card's own 24px padding, growing
   with content. Without field-sizing support it stays the legacy two-row box. */
html[data-theme="tonal"] #chat-input-textarea {
    display: block;
    margin-top: 0;
    min-height: 1.5rem;
    field-sizing: content;
}

html[data-theme="tonal"] .nadia-chat-refresh-composer #chat-input-textarea {
    margin-top: 0;
    min-height: --spacing(8);
    field-sizing: fixed;
}

html[data-theme="tonal"] .composer-input-area {
    min-height: 1.5rem;
}

textarea::-webkit-resizer {
    display: none;
}

textarea::placeholder {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
}

.chat-message-controls--disabled {
    @apply opacity-60;
}

.chat-message-controls--disabled,
.chat-message-controls--disabled * {
    cursor: not-allowed;
}

/* Send/recording controls retain their theme-specific art and animated geometry. */
.tonal-glyph {
    display: none;
}

html[data-theme="tonal"] .composer-bar :deep(.legacy-glyph) {
    display: none;
}

html[data-theme="tonal"] .composer-bar .tonal-glyph {
    display: block;
    width: 1.25rem;
    height: 1.25rem;
}

html[data-theme="tonal"] .composer-bar {
    min-height: 2.5rem;
}

html[data-theme="tonal"] .composer-bar div.flex {
    gap: 4px;
}

:deep() {
    .recorder-button--idle:disabled {
        @apply fill-composer-action-disabled;
    }
}

.recording-send-pulse {
    animation: send-pulse 2s ease-in-out infinite;
}

@keyframes send-pulse {
    0%,
    100% {
        transform: scale(1);
    }
    50% {
        transform: scale(1.2);
    }
}

.recording-dot {
    @apply inline-block h-2.5 w-2.5 rounded-full bg-recording-indicator;
    animation: recording-pulse 1.5s ease-in-out infinite;
}

@keyframes recording-pulse {
    0%,
    100% {
        opacity: 1;
    }
    50% {
        opacity: 0.3;
    }
}

.dropzone {
    /* Sits over the form's own 1px border so only the dashed line shows, in the composer's shape */
    @apply absolute -inset-px z-20 rounded-[inherit] border-2 border-dashed border-control-accent bg-surface-raised;
}

.fade-enter-active,
.fade-leave-active {
    transition: opacity 150ms ease-in-out;
}

.fade-enter-from,
.fade-leave-to {
    opacity: 0;
}

/* Unlayered so it beats body-small, whose 1.43 line-height grows with the 16px mobile font. */
.nadia-refresh-composer-textarea {
    @apply leading-6;
}

@layer components {
    .nadia-chat-refresh-composer {
        @apply flex flex-col gap-2 p-2 border border-control-border rounded-full shadow-md focus-field-within transition-[border-radius] duration-200 ease-in-out;
    }
    .nadia-chat-refresh-composer.nadia-chat-refresh-composer-expanded {
        @apply rounded-lgr;
    }
    .nadia-refresh-composer-row {
        @apply flex items-end gap-1;
    }
    .nadia-refresh-composer-input {
        @apply min-w-0 grow;
    }
    .nadia-refresh-composer-textarea {
        @apply block min-h-8 max-h-33 px-1 py-1 overflow-y-auto resize-none placeholder:text-control-placeholder;
    }
    /* Magenta is reserved for composer send and record (DESIGN.md), so it stays a composer-only override. */
    .composer-send {
        @apply bg-accent text-ink-inverse hover:bg-accent-hover disabled:bg-composer-action-disabled disabled:text-button-disabled-text;
    }
    @media (prefers-reduced-motion: no-preference) {
        .animate-mic-pulse {
            animation: mic-pulse 2s ease-in-out 0.5s 3;
        }
    }
}

@keyframes mic-pulse {
    0%,
    100% {
        box-shadow: 0 0 0 0 color-mix(in srgb, var(--color-accent) 45%, transparent);
    }
    50% {
        box-shadow: 0 0 0 6px transparent;
    }
}
</style>

<style scoped>
@reference "../../../css/tailwind.css";

/* Composer-only override, like .composer-send: the legacy recorder is a bare mic circle on mobile
   and a labelled magenta pill from md up, which no BaseButton theme draws. Scoped selectors outrank
   the primary theme, so these win at rest, on hover and when disabled. */
@layer components {
    .recorder-button {
        @apply flex items-center gap-1 rounded-none bg-transparent p-0 text-ink-inverse hover:bg-transparent disabled:bg-transparent disabled:text-ink-inverse md:rounded-lgr md:py-3 md:px-6;
    }

    .recorder-button--idle {
        @apply md:bg-accent md:hover:bg-accent-hover md:disabled:bg-accent;
    }

    .recorder-button--recording {
        @apply md:bg-recording-indicator md:hover:bg-recording-indicator/90 md:disabled:bg-recording-indicator;
    }
}
</style>
